import React, { memo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useUser } from 'Context/User';
import Uppy from '@uppy/core';
import Transloadit from '@uppy/transloadit';

import {
  AddMoistureLogModal,
  SelectMoistureLogMaterialScreen,
  AddNewMaterialReadingScreen,
  SelectDamageTypeScreen,
} from 'Components/RocketDry';
import { numberInputRegex } from 'Containers/RocketDry/helpers';

import { useRocketDryFunctions } from 'Context/RocketDry';
import { createDryingLog, setDryingGoal } from 'Containers/RocketDry/actions';
import { dryableDamagedMaterialsSelector } from 'Containers/RocketScan/selectors';

import { areEqual } from 'Utils/equalityChecks';

function AddMoistureLogModalContainer() {
  const dispatch = useDispatch();

  const {
    selectedRoomId,
    isAddMoistureLogMaterialModalOpen,
    setIsAddMoistureLogMaterialModalOpen,
    setMoistureLogRefetch,
  }: any = useRocketDryFunctions();

  const dryableDamagedMaterials = useSelector(dryableDamagedMaterialsSelector, areEqual);

  const { id: userId } = useUser();

  const [materialSelections, setMaterialSelections] = useState([]);
  const [selectedDamageMaterial, setSelectedDamageMaterial] = useState(undefined);

  const [goalAverage, setGoalAverage] = useState('');
  const [latestAverage, setLatestAverage] = useState('');
  const [photo, setPhoto] = useState<File>();
  const [removed, setRemoved] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const onClickDamageType = useCallback(
    (type: string) => {
      const filtered = dryableDamagedMaterials.filter((material) => material.damage_type.name === type);
      setMaterialSelections(filtered);
    },
    [dryableDamagedMaterials]
  );

  const onClickMaterial = useCallback((material: any) => {
    setSelectedDamageMaterial(material);
  }, []);

  const onChangeGoalAverage = useCallback(({ target: { value } }) => {
    const filtered = value.replace(numberInputRegex, '');
    setGoalAverage(filtered);
  }, []);

  const onChangeLatestAverage = useCallback(({ target: { value } }) => {
    const filtered = value.replace(numberInputRegex, '');
    setLatestAverage(filtered);
  }, []);

  const onRemovedCheckboxClick = useCallback(() => {
    setRemoved((value) => !value);
  }, []);

  const onCloseModal = useCallback(() => {
    setIsAddMoistureLogMaterialModalOpen(false);
    setMaterialSelections([]);
    setSelectedDamageMaterial(undefined);
    setGoalAverage('');
    setLatestAverage('');
    setPhoto(null);
    setRemoved(false);
    setIsProcessing(false);
  }, []);

  const onAddReadingButtonClick = useCallback(async () => {
    if (selectedRoomId && selectedDamageMaterial) {
      setIsProcessing(true);
      await dispatch(setDryingGoal(selectedRoomId, selectedDamageMaterial.id, goalAverage));
      const params: any = {
        removed,
      };
      if (!removed) {
        params.reading = latestAverage;
      }
      const addReadingResponse: any = await dispatch(
        createDryingLog(selectedRoomId, selectedDamageMaterial.id, params)
      );
      if (photo && addReadingResponse?.data?.id) {
        const uppy = new Uppy().use(Transloadit, {
          assemblyOptions: {
            params: {
              auth: { key: import.meta.env.VITE_TRANSLOADIT_AUTH_ID },
              template_id: import.meta.env.VITE_TRANSLOADIT_ATMOSPHERICLOG_TEMPLATE_ID,
              fields: {
                user_id: userId,
                source: 'webapp',
                moisture_log_id: addReadingResponse.data.id,
              },
            },
          },
          waitForEncoding: true,
        });

        uppy.on('transloadit:complete', () => {
          // wait a bit before refetching, database needs to be synced first
          setTimeout(() => {
            setMoistureLogRefetch(true);
            onCloseModal();
          }, 1000);
        });

        uppy.addFile({
          name: photo.name,
          type: photo.type,
          data: photo,
        });

        uppy.upload();
      } else {
        setMoistureLogRefetch(true);
        onCloseModal();
      }
    }
  }, [selectedRoomId, selectedDamageMaterial, userId, goalAverage, latestAverage, photo, removed]);

  return (
    <AddMoistureLogModal isOpen={isAddMoistureLogMaterialModalOpen} closeModal={onCloseModal}>
      {materialSelections.length === 0 && <SelectDamageTypeScreen onClick={onClickDamageType} />}
      {materialSelections.length > 0 && !selectedDamageMaterial && (
        <SelectMoistureLogMaterialScreen damageMaterials={materialSelections} onClick={onClickMaterial} />
      )}
      {selectedDamageMaterial && (
        <AddNewMaterialReadingScreen
          closeModal={onCloseModal}
          formErrors={{}}
          isProcessing={isProcessing}
          goalAverage={goalAverage}
          latestAverage={latestAverage}
          removed={removed}
          material={selectedDamageMaterial}
          photo={photo}
          onChangeGoalAverage={onChangeGoalAverage}
          onChangeLatestAverage={onChangeLatestAverage}
          onRemovedCheckboxClick={onRemovedCheckboxClick}
          setPhoto={setPhoto}
          onAddReadingButtonClick={onAddReadingButtonClick}
        />
      )}
    </AddMoistureLogModal>
  );
}

const AddMoistureLogModalMemo = memo(AddMoistureLogModalContainer, areEqual);

export { AddMoistureLogModalMemo as AddMoistureLogModal };
